// 百鬼図鑑（100体）。「妖」タブは章ごと（1章10体＝1画面）、「式神」タブは式神30体（エリート・章ボス）と効果の合計。
// 未遭遇は影で表示。記録は Profile.dex（倒した敵ID）と式神（Profile.shikigami）から。
import { CHAPTERS, DEX_LIST, ENEMIES, SHIKIGAMI, SHIKIGAMI_IDS, dexNo, type EnemyDef } from '@hyakki/core';
import { ENEMY_SPRITES, ROLE_STYLE, dexFitSpriteStyle, dexFitStyle, firstUsable, shikiArtHtml, spriteStyle } from '../assets';
import { artIcon, icon } from '../ui/icons';
import { register } from '../router';
import { state } from '../state';
import { esc } from '../ui/dom';
import { openModal } from '../ui/overlay';
import { onSwipe } from '../ui/fit';
import { bindShikiActions, ownedShikiCount, shikiDetailHtml, shikiListHtml, shikiStatusHtml, shikiTotalsHtml } from '../ui/shiki';
import { bindCompanionActions } from '../ui/companion';

const KANJI = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
let view: 'dex' | 'shiki' = 'dex';
let chapter = 1;

register('dex', {
  title: '百鬼図鑑',
  mount(el) {
    const p = state.profile;
    const dex = p?.dex as unknown;
    const seen = new Set<string>(Array.isArray(dex) ? (dex as string[]) : dex && typeof dex === 'object' ? Object.keys(dex as object).filter((k) => (dex as Record<string, number>)[k] > 0) : []);
    const known = (e: EnemyDef) => !p || seen.has(e.id);
    const found = DEX_LIST.filter(known).length;
    const nShiki = ownedShikiCount(p);
    // 妖の図鑑はいつも最初の姿（式神の進化の姿は式神のタブ・育成で出す）
    const cell = (e: EnemyDef) => {
      const k = known(e);
      return `<li><button class="dex-cell ${k ? '' : 'unknown'} ${e.role}" data-id="${e.id}" ${k ? '' : 'aria-label="未遭遇"'}>
        <small>No.${dexNo(e.id)}</small><span class="thumb">${k ? `<span class="dex-fit" style="${dexFitStyle(e.id, 1)}">${shikiArtHtml(e.id, 64, 1)}</span>` : `<span class="dex-fit" style="${dexFitSpriteStyle(e.id)}"><span class="dex-unknown" style="${enemySilhouette(e.id)}"></span></span>`}</span>
        <span class="dc-name">${k ? esc(e.name) : '？？？'}</span></button></li>`;
    };
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page dex">
      <header class="screen-head"><h1 class="screen-title">${artIcon('dex')}百鬼図鑑</h1><div class="end dex-count"><b>${found}</b><small>/ 100</small><div class="meter"><i style="width:${found}%"></i></div></div></header>
      <div class="tabs seg-tabs" role="tablist"><button class="tab-btn" role="tab" data-v="dex">${icon('dex')}妖</button><button class="tab-btn" role="tab" data-v="shiki">${icon('shiki')}式神<small class="dex-n">${nShiki}/${SHIKIGAMI_IDS.length}</small></button></div>
      <div class="dex-body"></div></section></div>`;
    const body = el.querySelector<HTMLElement>('.dex-body')!;
    const draw = () => {
      el.querySelectorAll<HTMLElement>('[data-v]').forEach((b) => {
        b.classList.toggle('on', b.dataset.v === view);
        b.setAttribute('aria-selected', String(b.dataset.v === view));
      });
      if (view === 'shiki') {
        body.className = 'dex-body dex-shiki-view';
        body.innerHTML = p
          ? `<div class="shiki-pane"><div class="sk-side"><h3 class="sec-title">効果の合計<small>${nShiki}/${SHIKIGAMI_IDS.length}体</small></h3>${shikiTotalsHtml(p)}</div><div class="sk-list fit-scroll">${shikiListHtml(p)}</div></div>`
          : '';
        return;
      }
      const list = DEX_LIST.filter((e) => e.chapter === chapter);
      body.className = 'dex-body dex-ch-view';
      body.innerHTML = `<div class="tabs dex-ch-tabs" role="tablist" aria-label="章">${CHAPTERS.map((c) => {
        const n = DEX_LIST.filter((e) => e.chapter === c.no && known(e)).length;
        return `<button class="tab-btn ${c.no === chapter ? 'on' : ''}" role="tab" aria-selected="${c.no === chapter}" data-ch="${c.no}" title="${esc(c.name)}">第${KANJI[c.no - 1]}章<small>${n}/${DEX_LIST.filter((e) => e.chapter === c.no).length}</small></button>`;
      }).join('')}</div>
        <h2 class="sec-title dex-ch-name">第${KANJI[chapter - 1]}章 ${esc(CHAPTERS[chapter - 1]?.name ?? '')}</h2>
        <ul class="dex-grid" data-swipe-ch>${list.map(cell).join('')}</ul>`;
      body.querySelector('.dex-ch-tabs .on')?.scrollIntoView({ block: 'nearest', inline: 'center' });
    };
    draw();
    const offSwipe = onSwipe(el, '[data-swipe-ch]', (d) => {
      chapter = Math.min(CHAPTERS.length, Math.max(1, chapter + d));
      draw();
    });
    const click = (ev: Event) => {
      const t = ev.target as HTMLElement;
      const v = t.closest<HTMLElement>('[data-v]');
      if (v) {
        view = v.dataset.v as typeof view;
        return draw();
      }
      const ch = t.closest<HTMLElement>('[data-ch]');
      if (ch) {
        chapter = Number(ch.dataset.ch);
        return draw();
      }
      const sk = t.closest<HTMLElement>('[data-shiki]');
      const b = sk ?? t.closest<HTMLElement>('[data-id]');
      if (!b || b.classList.contains('unknown')) return;
      const e = ENEMIES[sk ? sk.dataset.shiki! : b.dataset.id!];
      if (!e) return;
      // 式神（エリート・章ボス）は育成の詳細（式神Lv・霊玉で強化・★ごとのスキル）。小型・中型は図鑑の記録と「式神にはならない」
      const isShiki = !!SHIKIGAMI[e.id];
      const html = () =>
        isShiki
          ? `<div class="dex-detail is-shiki">${shikiDetailHtml(state.profile, e.id)}<p class="muted small">No.${dexNo(e.id)}・初登場 第${e.chapter}章 ${esc(CHAPTERS[e.chapter - 1].name)}</p></div>`
          : `<div class="dex-detail"><span class="thumb dd-art"><span class="dex-fit" style="${dexFitStyle(e.id, 1)}">${shikiArtHtml(e.id, 120, 1)}</span></span><p>No.${dexNo(e.id)}・${ROLE_STYLE[e.role].label}（${e.kind === 'boss' || e.kind === 'rival' ? '章ボス' : '中立妖怪'}）・初登場 第${e.chapter}章 ${esc(CHAPTERS[e.chapter - 1].name)}</p>
         ${shikiStatusHtml(p, e.id)}</div>`;
      const m = openModal(html(), { title: isShiki ? `式神 ${e.name}` : e.name, className: isShiki ? 'skd-modal' : '' });
      bindCompanionActions(m.body, () => m.close()); // 「連れていく」
      bindShikiActions(m.body, () => {
        if (!m.el.isConnected) return;
        m.body.innerHTML = html();
        draw();
      });
    };
    el.addEventListener('click', click);
    return () => {
      offSwipe();
      el.removeEventListener('click', click);
    };
  },
});

/** 未遭遇：絵を影（黒）で見せる */
function enemySilhouette(id: string) {
  const ref = firstUsable(ENEMY_SPRITES[id]);
  return ref ? spriteStyle(ref) : '';
}
